import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ArrowUpRight } from "lucide-react";
import { COMPANY } from "../../data/content";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/services", label: "Services" },
  { to: "/why-choose-us", label: "Why Choose Us" },
  { to: "/industries", label: "Industries" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-300 ${
          scrolled
            ? "bg-white/80 backdrop-blur-xl border-b border-sml-border py-3"
            : "bg-transparent border-b border-transparent py-5"
        }`}
      >
        <div className="mx-auto max-w-7xl px-5 lg:px-8 flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center group">
            <img
              src="/logo.png"
              alt="SML Property Solutions"
              className={`w-auto rounded-sm bg-white transition-all duration-300 ${scrolled ? "h-12" : "h-14 shadow-md"}`}
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative text-sm font-medium tracking-tight transition-colors hover:text-sml-forest ${
                    isActive ? "text-sml-forest" : "text-sml-charcoal/70"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={COMPANY.phoneHref}
              data-testid="header-call-link"
              className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-sml-charcoal/80 hover:text-sml-forest transition-colors"
            >
              <Phone className="h-4 w-4" />
              {COMPANY.phone}
            </a>
            <button
              onClick={() => navigate("/contact")}
              data-testid="header-quote-button"
              className="hidden sm:inline-flex items-center gap-2 rounded-sm bg-sml-forest text-white text-sm font-medium px-5 py-2.5 hover:bg-sml-green transition-colors active:scale-[0.98]"
            >
              Request a Quote
              <ArrowUpRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-open"
              className="lg:hidden grid place-items-center h-10 w-10 rounded-sm border border-sml-border text-sml-charcoal"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-sml-forest text-white lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-5 py-5 border-b border-white/10">
              <img src="/logo.png" alt="SML Property Solutions" className="h-12 w-auto rounded-sm bg-white p-0.5" />
              <button
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close"
                className="grid place-items-center h-10 w-10 rounded-sm border border-white/20"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-col px-5 py-6">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="block font-display text-3xl py-3 border-b border-white/10"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="px-5 mt-4 space-y-3">
              <a
                href={COMPANY.phoneHref}
                data-testid="mobile-call-link"
                className="flex items-center justify-center gap-2 rounded-sm border border-white/25 py-3 font-medium"
              >
                <Phone className="h-4 w-4" /> Call {COMPANY.phone}
              </a>
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                data-testid="mobile-quote-button"
                className="flex items-center justify-center gap-2 rounded-sm bg-sml-bronze text-sml-forest py-3 font-semibold"
              >
                Request a Quote <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
